import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { isProductionDomain } from '@/lib/productionDomainCheck';
import { isLikelyBot } from '@/lib/botDetection';

const VISITOR_ID_KEY = 'xpand_visitor_id';
const SESSION_ID_KEY = 'xpand_session_id';
const RAGE_CLICK_THRESHOLD = 3;
const RAGE_CLICK_WINDOW_MS = 500;
const SCROLL_THROTTLE_MS = 100;

function getVisitorId(): string {
  let id = localStorage.getItem(VISITOR_ID_KEY);
  if (!id) {
    id = `v_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(VISITOR_ID_KEY, id);
  }
  return id;
}

function getSessionId(): string {
  const stored = sessionStorage.getItem(SESSION_ID_KEY);
  if (stored) {
    try {
      const { id } = JSON.parse(stored);
      return id;
    } catch {
      // Continue to create new
    }
  }
  return `s_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

interface ClickData {
  x: number;
  y: number;
  timestamp: number;
  element: HTMLElement;
}

/**
 * Advanced tracking hook for heatmaps, rage clicks, scroll depth,
 * engagement scoring, friction detection, and form analytics
 */
export function useAdvancedTracking() {
  const location = useLocation();
  const isProduction = useRef<boolean>(false);
  const pageStartTime = useRef<number>(Date.now());
  const hasScrolled = useRef<boolean>(false);
  const lastScrollTrack = useRef<number>(0);
  const scrollDataSaved = useRef<boolean>(false);
  
  // Scroll tracking state
  const scrollMilestones = useRef({ 25: false, 50: false, 75: false, 100: false });
  const scrollTimes = useRef<Record<number, number>>({});
  const maxScrollPercent = useRef<number>(0);
  const scrollDirectionChanges = useRef<number>(0);
  const lastScrollDirection = useRef<'up' | 'down' | null>(null);
  const lastScrollY = useRef<number>(0);
  const totalScrollDistance = useRef<number>(0);
  
  // Click tracking for rage clicks
  const recentClicks = useRef<ClickData[]>([]);
  const clickBuffer = useRef<ClickData[]>([]);
  
  // Engagement tracking
  const interactionCount = useRef<number>(0);
  const lastActivityTime = useRef<number>(Date.now());

  // Initialize production check - check dynamically each time
  const checkProduction = useCallback(() => {
    return isProductionDomain() && !isLikelyBot();
  }, []);

  // Track click with coordinates for heatmap
  const trackClick = useCallback(async (e: MouseEvent) => {
    if (!checkProduction()) return;

    const target = e.target as HTMLElement;
    const now = Date.now();
    
    const clickData: ClickData = {
      x: e.clientX,
      y: e.clientY + window.scrollY, // Absolute Y position including scroll
      timestamp: now,
      element: target,
    };

    // Add to recent clicks for rage click detection
    recentClicks.current.push(clickData);
    recentClicks.current = recentClicks.current.filter(
      c => now - c.timestamp < RAGE_CLICK_WINDOW_MS
    );

    // Detect rage clicks (multiple rapid clicks in same area)
    const isRageClick = recentClicks.current.length >= RAGE_CLICK_THRESHOLD;
    
    // Buffer clicks and batch insert every few seconds
    clickBuffer.current.push(clickData);
    interactionCount.current++;
    lastActivityTime.current = now;

    // Insert rage clicks immediately for real-time visibility
    if (isRageClick) {
      try {
        await supabase.from('click_heatmap').insert({
          session_id: getSessionId(),
          visitor_id: getVisitorId(),
          page_path: location.pathname,
          click_x: Math.round(e.clientX),
          click_y: Math.round(e.clientY + window.scrollY),
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
          page_width: document.documentElement.scrollWidth,
          page_height: document.documentElement.scrollHeight,
          element_tag: target.tagName.toLowerCase(),
          element_id: target.id || null,
          element_class: target.className?.toString()?.slice(0, 200) || null,
          element_text: target.textContent?.slice(0, 100) || null,
          is_rage_click: true,
          click_count: recentClicks.current.length,
        });

        // Also track as friction event
        await supabase.from('friction_events').insert({
          session_id: getSessionId(),
          visitor_id: getVisitorId(),
          page_path: location.pathname,
          friction_type: 'rage_click',
          element_selector: target.id ? `#${target.id}` : target.className?.toString()?.split(' ')[0] || target.tagName,
          event_count: recentClicks.current.length,
          metadata: {
            x: e.clientX,
            y: e.clientY,
            element_tag: target.tagName,
          },
        });
      } catch (error) {
        console.error('[Advanced Tracking] Rage click error:', error);
      }
    }
  }, [location.pathname, checkProduction]);

  // Flush click buffer periodically
  const flushClickBuffer = useCallback(async () => {
    if (!checkProduction() || clickBuffer.current.length === 0) return;

    const clicks = [...clickBuffer.current];
    clickBuffer.current = [];

    try {
      const clickRecords = clicks.map(click => ({
        session_id: getSessionId(),
        visitor_id: getVisitorId(),
        page_path: location.pathname,
        click_x: Math.round(click.x),
        click_y: Math.round(click.y),
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        page_width: document.documentElement.scrollWidth,
        page_height: document.documentElement.scrollHeight,
        element_tag: click.element.tagName.toLowerCase(),
        element_id: click.element.id || null,
        element_class: click.element.className?.toString()?.slice(0, 200) || null,
        element_text: click.element.textContent?.slice(0, 100) || null,
        is_rage_click: false,
        click_count: 1,
      }));

      await supabase.from('click_heatmap').insert(clickRecords);
    } catch (error) {
      console.error('[Advanced Tracking] Click buffer flush error:', error);
    }
  }, [location.pathname, checkProduction]);

  // Track scroll depth with throttling
  const trackScroll = useCallback(() => {
    if (!checkProduction()) return;

    const now = Date.now();
    // Throttle scroll tracking
    if (now - lastScrollTrack.current < SCROLL_THROTTLE_MS) return;
    lastScrollTrack.current = now;

    hasScrolled.current = true;
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    
    // Calculate scroll percentage properly
    let scrollPercent = 0;
    if (docHeight > 0) {
      scrollPercent = Math.min(100, Math.round((scrollTop / docHeight) * 100));
    } else if (scrollTop === 0) {
      // Page fits in viewport, consider it 100% viewed
      scrollPercent = 100;
    }
    
    // Track scroll distance
    const scrollDelta = Math.abs(scrollTop - lastScrollY.current);
    if (scrollDelta > 0) {
      totalScrollDistance.current += scrollDelta;
    }
    
    // Track direction changes
    if (scrollDelta > 5) { // Minimum threshold to count as a direction
      const currentDirection = scrollTop > lastScrollY.current ? 'down' : 'up';
      if (lastScrollDirection.current && currentDirection !== lastScrollDirection.current) {
        scrollDirectionChanges.current++;
      }
      lastScrollDirection.current = currentDirection;
    }
    lastScrollY.current = scrollTop;

    // Update max scroll
    if (scrollPercent > maxScrollPercent.current) {
      maxScrollPercent.current = scrollPercent;
    }

    // Track milestones
    const milestones = [25, 50, 75, 100] as const;
    
    for (const milestone of milestones) {
      if (scrollPercent >= milestone && !scrollMilestones.current[milestone]) {
        scrollMilestones.current[milestone] = true;
        scrollTimes.current[milestone] = now - pageStartTime.current;
      }
    }

    lastActivityTime.current = now;
  }, [checkProduction]);

  // Save scroll data on page exit - only if user actually scrolled
  const saveScrollData = useCallback(async () => {
    if (!checkProduction()) return;
    if (scrollDataSaved.current) return; // Prevent duplicate saves
    
    // Only save meaningful scroll data
    const hasScrollData = hasScrolled.current || maxScrollPercent.current > 0 || totalScrollDistance.current > 0;
    if (!hasScrollData) return;

    scrollDataSaved.current = true;

    try {
      await supabase.from('scroll_heatmap').insert({
        session_id: getSessionId(),
        visitor_id: getVisitorId(),
        page_path: location.pathname,
        max_scroll_percent: maxScrollPercent.current,
        scroll_25: scrollMilestones.current[25],
        scroll_50: scrollMilestones.current[50],
        scroll_75: scrollMilestones.current[75],
        scroll_100: scrollMilestones.current[100],
        time_to_25_ms: scrollTimes.current[25] || null,
        time_to_50_ms: scrollTimes.current[50] || null,
        time_to_75_ms: scrollTimes.current[75] || null,
        time_to_100_ms: scrollTimes.current[100] || null,
        total_scroll_distance: Math.round(totalScrollDistance.current),
        scroll_direction_changes: scrollDirectionChanges.current,
        viewport_height: window.innerHeight,
        page_height: document.documentElement.scrollHeight,
      });
    } catch (error) {
      console.error('[Advanced Tracking] Scroll save error:', error);
    }
  }, [location.pathname, checkProduction]);

  // Update engagement score
  const updateEngagementScore = useCallback(async () => {
    if (!checkProduction()) return;

    const timeOnPage = Date.now() - pageStartTime.current;
    
    // Calculate scores
    const timeScore = Math.min(Math.floor(timeOnPage / 10000), 10); // Max 10 points for 100s+
    const scrollScore = Math.floor(maxScrollPercent.current / 10); // Max 10 points
    const clickScore = Math.min(interactionCount.current, 10); // Max 10 points
    const pageDepthScore = 3; // Fixed for now, could track across session
    
    const totalScore = timeScore + scrollScore + clickScore + pageDepthScore;
    
    // Determine engagement level
    let engagementLevel = 'low';
    if (totalScore >= 25) engagementLevel = 'high';
    else if (totalScore >= 15) engagementLevel = 'medium';

    const isHighIntent = maxScrollPercent.current >= 75 && interactionCount.current >= 3;

    try {
      await supabase.from('engagement_scores').upsert({
        session_id: getSessionId(),
        visitor_id: getVisitorId(),
        total_score: totalScore,
        time_score: timeScore,
        scroll_score: scrollScore,
        click_score: clickScore,
        page_depth_score: pageDepthScore,
        engagement_level: engagementLevel,
        is_high_intent: isHighIntent,
        total_time_ms: timeOnPage,
        last_active_at: new Date().toISOString(),
      }, { onConflict: 'session_id' });
    } catch (error) {
      console.error('[Advanced Tracking] Engagement score error:', error);
    }
  }, [checkProduction]);

  // Track form field interactions
  const trackFormField = useCallback(async (e: FocusEvent | Event, eventType: string) => {
    if (!checkProduction()) return;

    const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    if (!target.tagName) return;

    const tagName = target.tagName.toLowerCase();
    if (!['input', 'textarea', 'select'].includes(tagName)) return;

    const form = target.closest('form');

    try {
      await supabase.from('form_analytics').insert({
        session_id: getSessionId(),
        visitor_id: getVisitorId(),
        page_path: location.pathname,
        form_id: form?.id || null,
        form_name: form?.getAttribute('name') || null,
        field_name: target.name || target.id || 'unknown',
        field_type: (target as HTMLInputElement).type || tagName,
        event_type: eventType,
      });
    } catch (error) {
      console.error('[Advanced Tracking] Form tracking error:', error);
    }
  }, [location.pathname]);

  // Track exit intent
  const trackExitIntent = useCallback(async (e: MouseEvent) => {
    if (!isProduction.current) return;

    // Detect exit intent (mouse leaving viewport at top)
    if (e.clientY <= 0) {
      try {
        await supabase.from('exit_intents').insert({
          session_id: getSessionId(),
          visitor_id: getVisitorId(),
          page_path: location.pathname,
          exit_type: 'mouse_leave_top',
          time_on_page_ms: Date.now() - pageStartTime.current,
          scroll_depth: maxScrollPercent.current,
          mouse_position_x: e.clientX,
          mouse_position_y: e.clientY,
        });
      } catch (error) {
        console.error('[Advanced Tracking] Exit intent error:', error);
      }
    }
  }, [location.pathname]);

  // Reset on page change
  useEffect(() => {
    // Save previous page data before resetting
    const saveAndReset = async () => {
      await flushClickBuffer();
      await saveScrollData();
      await updateEngagementScore();
      
      // Reset tracking state for new page
      pageStartTime.current = Date.now();
      scrollMilestones.current = { 25: false, 50: false, 75: false, 100: false };
      scrollTimes.current = {};
      maxScrollPercent.current = 0;
      scrollDirectionChanges.current = 0;
      lastScrollDirection.current = null;
      lastScrollY.current = 0;
      totalScrollDistance.current = 0;
      recentClicks.current = [];
      clickBuffer.current = [];
      interactionCount.current = 0;
      hasScrolled.current = false;
      scrollDataSaved.current = false;
    };
    
    saveAndReset();
  }, [location.pathname, flushClickBuffer, saveScrollData, updateEngagementScore]);

  // Set up event listeners
  useEffect(() => {
    // Check production dynamically
    if (!checkProduction()) return;

    document.addEventListener('click', trackClick);
    window.addEventListener('scroll', trackScroll, { passive: true });
    document.addEventListener('focusin', (e) => trackFormField(e, 'focus'));
    document.addEventListener('focusout', (e) => trackFormField(e, 'blur'));
    document.addEventListener('mouseout', trackExitIntent);

    // Periodic flush
    const flushInterval = setInterval(flushClickBuffer, 10000);
    const engagementInterval = setInterval(updateEngagementScore, 30000);

    return () => {
      document.removeEventListener('click', trackClick);
      window.removeEventListener('scroll', trackScroll);
      document.removeEventListener('mouseout', trackExitIntent);
      clearInterval(flushInterval);
      clearInterval(engagementInterval);
    };
  }, [trackClick, trackScroll, trackFormField, trackExitIntent, flushClickBuffer, updateEngagementScore, checkProduction]);

  // Save on unload
  useEffect(() => {
    const handleUnload = () => {
      flushClickBuffer();
      saveScrollData();
      updateEngagementScore();
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [flushClickBuffer, saveScrollData, updateEngagementScore]);
}
